import { useState, useEffect, useRef } from "react";
import AdminPage from "./AdminPage";
import ConsumerFlow from "./ConsumerFlow";

const API_BASE = "https://capitaliq-backend-production-bff2.up.railway.app";

const STEPS = {
  ABN:"abn", CONFIRM:"confirm", ASSET:"asset", ENRICHING:"enriching", ENRICHED:"enriched",
  PRIVACY:"privacy", DIRECTORS:"directors", BUSINESS:"business", LIABILITIES:"liabilities",
  PROPERTY:"property", SUMMARY:"summary", MATCHING:"matching", RESULTS:"results"
};

// ─── Formatters ───────────────────────────────────────────────────────────────
const formatABN = (v) => { const d=v.replace(/\D/g,"").slice(0,11); if(d.length<=2)return d; if(d.length<=5)return`${d.slice(0,2)} ${d.slice(2)}`; if(d.length<=8)return`${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5)}`; return`${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5,8)} ${d.slice(8)}`; };
const cleanABN = (v) => v.replace(/\D/g,"");
const formatCurrency = (v) => { const n=v.replace(/\D/g,""); return n?parseInt(n).toLocaleString("en-AU"):""; };
const formatDOB = (v) => { const d=v.replace(/\D/g,"").slice(0,8); if(d.length<=2)return d; if(d.length<=4)return`${d.slice(0,2)}/${d.slice(2)}`; return`${d.slice(0,2)}/${d.slice(2,4)}/${d.slice(4)}`; };

function isValidABN(abn) {
  const d=abn.replace(/\D/g,""); if(d.length!==11)return false;
  const w=[10,1,3,5,7,9,11,13,15,17,19],digs=d.split("").map(Number); digs[0]-=1;
  return digs.reduce((a,n,i)=>a+n*w[i],0)%89===0;
}

async function lookupABN(abn) {
  const r=await fetch(`${API_BASE}/api/abn/${abn}`); const d=await r.json();
  if(!r.ok)throw new Error(d.error||"Lookup failed"); return d;
}

async function enrichAsset(description,amount) {
  const r=await fetch(`${API_BASE}/api/enrich`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({description,amount})});
  const d=await r.json(); if(!r.ok)throw new Error(d.error||"Enrichment failed"); return d;
}

const emptyDirector = () => ({name:"",dob:"",address:"",ownership:""});

// ─── LENDER MATCHING ENGINE ───────────────────────────────────────────────────
// Returns array of { tier: "strong"|"possible"|"declined", gaps: [], strengths: [] }
// Lender names are NEVER shown to the client — only counts and guidance

function classifyAsset(enriched) {
  const cat = (enriched?.category||"").toLowerCase();
  const type = (enriched?.assetType||"").toLowerCase();
  const make = (enriched?.make||"").toLowerCase();

  // Tier II brands for flexicommercial
  const tier2Brands = ["foton","sinotruk","sitrak","bonluck","higer","king long","yutong","zhongtong","dongfeng","gwm","ldv","mahindra","hangcha","sany","sunward","weichai","xcmg","boleo","luigong","sdlg","shantui","zoomlion"];
  const isTier2Brand = tier2Brands.some(b => make.includes(b));

  // Primary: trucks, trailers, commercial vehicles, agri, earthmoving, forklifts, access equipment
  const primaryKeywords = ["truck","prime mover","trailer","excavator","dozer","grader","scraper","skid steer","wheel loader","backhoe","forklift","telehandler","boom lift","scissor lift","spider lift","tractor","harvester","header","cotton picker","baler","sprayer","ute","van","4wd","light commercial","crane"];
  const secondaryKeywords = ["medical","dental","laboratory","mining","compressor","generator","printing","packaging","forestry","woodworking","metalworking","workshop","mechanical"];
  const tertiaryKeywords = ["it","computer","software","fitness","drone","pos","audio","video","racking","security","fit out","air conditioning","catering","coffee","furniture","solar","spray booth"];

  let assetTier = "primary";
  if (tertiaryKeywords.some(k => cat.includes(k) || type.includes(k))) assetTier = "tertiary";
  else if (secondaryKeywords.some(k => cat.includes(k) || type.includes(k))) assetTier = "secondary";
  if (isTier2Brand) assetTier = "secondary"; // tier 2 brands = secondary for flexi

  const isPrimeMover = cat.includes("prime mover") || cat.includes("prime-mover");
  const isAgri = cat.includes("tractor")||cat.includes("harvester")||cat.includes("header")||cat.includes("sprayer")||cat.includes("baler")||cat.includes("seeder")||type.includes("agri");
  const isPassengerVehicle = cat.includes("car")||cat.includes("sedan")||cat.includes("suv")||cat.includes("hatchback");
  const isTruck = cat.includes("truck")||cat.includes("prime mover")||cat.includes("rigid");
  const isHeavyTruck = isTruck && !cat.includes("light");

  return { assetTier, isPrimeMover, isAgri, isPassengerVehicle, isTruck, isHeavyTruck, isTier2Brand };
}

function parseYearsTrading(yt) {
  if (!yt) return 0;
  if (yt === "Less than 1") return 0.5;
  if (yt === "10+") return 10;
  return parseInt(yt) || 0;
}

function parseAmount(a) { return parseInt((a||"0").toString().replace(/\D/g,""))||0; }

function matchLenders(bizData, enriched, businessProfile, directors, liabilities, propertyOwned) {
  const amount = parseAmount(enriched?.amount);
  const years = parseYearsTrading(businessProfile?.yearsTrading);
  const hasATO = businessProfile?.atoDebt === true;
  const hasProperty = propertyOwned === "yes" || propertyOwned === "spouse";
  const gstRegistered = bizData?.gst?.toLowerCase().includes("registered");
  const asset = classifyAsset(enriched);
  const assetAge = parseInt(enriched?.age) || 0;
  const entityType = (bizData?.entityType||"").toLowerCase();
  const isSoleTrader = entityType.includes("sole trader") || entityType.includes("individual");

  const results = [];

  // ── WESTPAC ──────────────────────────────────────────────────────────────────
  (() => {
    const gaps = [], strengths = [], notes = [];
    if (years >= 2) strengths.push("2+ years trading ✓");
    else gaps.push("Westpac requires 2+ years in business");
    if (!hasATO) strengths.push("No ATO debt ✓");
    else gaps.push("ATO debt or payment arrangements are not accepted");
    if (asset.isPrimeMover && amount > 150000) gaps.push("Prime movers above $150k require referral to standard assessment");
    if (asset.assetTier === "primary" && assetAge > 5) gaps.push(`Asset age of ~${assetAge} years may exceed the 5-year limit for this asset type`);
    if (asset.assetTier === "tertiary" && assetAge > 7) gaps.push("Agricultural assets must be under 7 years old");
    if (amount > 150000) notes.push("Amounts above $150k require existing Westpac relationship or referral");
    if (amount <= 150000 && years >= 2 && !hasATO) strengths.push("Eligible for DriveXpress fast-track ✓");

    const tier = gaps.length === 0 ? "strong" : gaps.length <= 1 ? "possible" : "declined";
    results.push({ lender: "Lender A", tier, gaps, strengths, notes });
  })();

  // ── ANGLE FINANCE ──────────────────────────────────────────────────────────
  (() => {
    const gaps = [], strengths = [], notes = [];
    if (years >= 2) strengths.push("2+ years ABN ✓");
    else if (years >= 0.25) { gaps.push("ABN under 2 years — Start-Up product available up to $150k with 20% deposit"); }
    else gaps.push("Business must be operating for at least 3 months");
    if (isSoleTrader && amount > 400000) gaps.push("Sole traders excluded from $400k Low Doc — company or trust structure preferred");
    if (asset.isPrimeMover) { notes.push("Prime Movers require 5+ years ABN, company/trust only, property backed, credit score 600+"); if(years<5) gaps.push("Prime Movers require 5+ years ABN"); }
    if (asset.assetTier === "tertiary" && amount > 400000) gaps.push("Tertiary assets excluded from $400k Low Doc");
    if (amount <= 100000) strengths.push("Under $100k — streamlined low doc, no GST required ✓");
    if (amount > 100000 && amount <= 400000) notes.push("$100k–$400k requires 1+ year GST and credit reference or mortgage statements");
    if (!hasATO) strengths.push("No ATO debt ✓");
    else if (amount >= 250000) gaps.push("ATO portals required for $250k+ — ATO debt may affect approval");
    if (amount <= 400000 && years >= 2 && !asset.isPrimeMover) strengths.push("Within Low Doc $400k limit ✓");

    const tier = gaps.length === 0 ? "strong" : gaps.length <= 1 ? "possible" : "declined";
    results.push({ lender: "Lender B", tier, gaps, strengths, notes });
  })();

  // ── NAB ──────────────────────────────────────────────────────────────────────
  (() => {
    const gaps = [], strengths = [], notes = [];
    if (years >= 2) strengths.push("2+ years trading ✓");
    else gaps.push("NAB requires 2+ years trading");
    if (amount <= 500000) strengths.push("Within $500k simplified limit ✓");
    else gaps.push("NAB simplified EF is capped at $500k");
    if (asset.assetTier === "primary" && assetAge <= 7) strengths.push("Asset age within policy ✓");
    else if (assetAge > 7) gaps.push(`Asset age of ~${assetAge} years exceeds NAB's 7-year limit for this category`);
    if (!hasATO) strengths.push("Statutory payments clear ✓");
    else if (amount > 150000) gaps.push("ATO must be clear for loans above $150k");
    if (amount <= 150000) notes.push("Up to $150k requires one of: real property ownership, 6 months bank statements, or 25% deposit");
    if (amount > 150000) notes.push("Above $150k requires property ownership OR bank statements OR 25% deposit, plus ATO clear");

    const tier = gaps.length === 0 ? "strong" : gaps.length <= 1 ? "possible" : "declined";
    results.push({ lender: "Lender C", tier, gaps, strengths, notes });
  })();

  // ── METRO FINANCE ─────────────────────────────────────────────────────────
  (() => {
    const gaps = [], strengths = [], notes = [];
    // Metro requires 2yr GST — we use years trading as proxy
    if (years >= 2) strengths.push("2+ years GST registration ✓");
    else gaps.push("Metro requires 2+ years continuous GST registration");

    // Agri requires 5yr GST
    if (asset.isAgri && years < 5) gaps.push("Metro Agri requires 5+ years GST registration");
    if (asset.isAgri && years >= 5) strengths.push("Meets Agri 5-year GST requirement ✓");

    // Credit reference required
    notes.push("A comparable asset finance credit reference (12 months satisfactory) is required");

    // Property
    if (hasProperty) strengths.push("Property backing noted ✓");
    else if (asset.isHeavyTruck || asset.isAgri) gaps.push("Property ownership required for trucks and agri equipment");
    else notes.push("Non-property owners can access motor vehicles up to $100k with 30% deposit (dealer only)");

    // Amount limits
    const maxLoan = asset.isAgri ? 250000 : asset.isHeavyTruck ? 250000 : 150000;
    if (amount <= maxLoan) strengths.push(`Within Metro's $${(maxLoan/1000).toFixed(0)}k limit for this asset type ✓`);
    else gaps.push(`Amount of $${amount.toLocaleString("en-AU")} exceeds Metro's limit for this asset type`);

    // Asset age
    const maxAge = asset.isPassengerVehicle ? 12 : 15;
    if (assetAge <= maxAge) strengths.push("Asset age within Metro policy ✓");
    else gaps.push(`Asset age of ~${assetAge} years exceeds Metro's ${maxAge}-year EOT limit`);

    const tier = gaps.length === 0 ? "strong" : gaps.length <= 1 ? "possible" : "declined";
    results.push({ lender: "Lender D", tier, gaps, strengths, notes });
  })();

  // ── FLEXICOMMERCIAL ──────────────────────────────────────────────────────
  (() => {
    const gaps = [], strengths = [], notes = [];
    if (years >= 2) strengths.push("2+ years ABN & GST ✓");
    else gaps.push("flexicommercial requires 2+ years ABN and GST");

    // No SUVs/passenger cars
    if (asset.isPassengerVehicle) { gaps.push("Passenger cars and SUVs are not funded"); }

    // Asset tier limits
    const tierLimits = { primary: 500000, secondary: 300000, tertiary: 300000 };
    const limit = tierLimits[asset.assetTier] || 300000;
    if (amount <= limit) strengths.push(`Within ${asset.assetTier} asset limit of $${(limit/1000).toFixed(0)}k ✓`);
    else gaps.push(`Amount exceeds flexicommercial's $${(limit/1000).toFixed(0)}k limit for ${asset.assetTier} assets`);

    // Property/deposit for $50k+
    if (amount >= 50000) {
      if (hasProperty) strengths.push("Property backing noted ✓");
      else notes.push("For amounts above $50k, property backing or 20% deposit is required");
    }

    // Transport operators must be asset backed
    if (asset.isTruck) notes.push("Transport operators/subcontractors must be asset backed with sufficient equity");

    // Credit reference for $300k+
    if (amount >= 300000) notes.push("9 months repayment history on a comparable asset finance contract required");
    if (amount >= 500000) notes.push("18 months repayment history required for $500k+ primary assets");

    // Asset age
    const maxAge = asset.assetTier === "primary" ? 20 : 7;
    if (assetAge <= maxAge) strengths.push("Asset age within policy ✓");
    else gaps.push(`Asset age of ~${assetAge} years exceeds flexicommercial's ${maxAge}-year EOT limit for ${asset.assetTier} assets`);

    if (isSoleTrader) strengths.push("Sole traders accepted ✓");

    const tier = gaps.length === 0 ? "strong" : gaps.length <= 1 ? "possible" : "declined";
    results.push({ lender: "Lender E", tier, gaps, strengths, notes });
  })();

  return results;
}

function buildMatchSummary(results) {
  const strong = results.filter(r => r.tier === "strong").length;
  const possible = results.filter(r => r.tier === "possible").length;
  const declined = results.filter(r => r.tier === "declined").length;

  // Collect all unique gaps across possible matches
  const possibleGaps = [...new Set(results.filter(r=>r.tier==="possible").flatMap(r=>r.gaps))];
  // Collect all unique gaps across declined
  const declinedGaps = [...new Set(results.filter(r=>r.tier==="declined").flatMap(r=>r.gaps))];

  return { strong, possible, declined, possibleGaps, declinedGaps };
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [step, setStep] = useState(STEPS.ABN);
  const [mode, setMode] = useState(null);
  const [abn, setAbn] = useState("");
  const [abnError, setAbnError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bizData, setBizData] = useState(null);
  const [assetDesc, setAssetDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [enriched, setEnriched] = useState(null);
  const [chatLog, setChatLog] = useState([]);
  const chatRef = useRef(null);
  const [directorCount, setDirectorCount] = useState(null);
  const [directors, setDirectors] = useState([emptyDirector()]);
  const [currentDirectorIdx, setCurrentDirectorIdx] = useState(0);
  const [directorDraft, setDirectorDraft] = useState(emptyDirector());
  const [business, setBusiness] = useState({yearsTrading:"",turnover:"",atoDebt:null});
  const [liabilities, setLiabilities] = useState({hasOther:null,details:""});
  const [matchResults, setMatchResults] = useState(null);

  const [propertyOwned, setPropertyOwned] = useState(null);
  const [submitted, setSubmitted] = useState(false);   const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isReturnAsset, setIsReturnAsset] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => { if(chatRef.current) chatRef.current.scrollTop=chatRef.current.scrollHeight; }, [chatLog]);
  

  const addMessage = (role, content, extra={}) =>
    setChatLog(prev=>[...prev,{role,content,...extra,id:Date.now()+Math.random()}]);
  const removeTyping = () => setChatLog(prev=>prev.filter(m=>!m.typing));

  // ─── ABN ─────────────────────────────────────────────────────────────────
  const handleABNLookup = async () => {
    const raw=cleanABN(abn);
    if(raw.length!==11){setAbnError("Please enter a valid 11-digit ABN.");return;}
    if(!isValidABN(raw)){setAbnError("That ABN doesn't look valid — check the numbers and try again.");return;}
    setAbnError("");setLoading(true);
    addMessage("user",`ABN: ${formatABN(raw)}`);
    await new Promise(r=>setTimeout(r,300));
    addMessage("bot","Give me a sec — looking that up in the ABR now…",{typing:true});
    try {
      const result=await lookupABN(raw); removeTyping();
      if(!result.found){addMessage("bot",`Hmm — couldn't find that ABN. Double-check and give it another go?`);setLoading(false);return;}
      setBizData(result);
      const d=result;
      const tradingLine=d.businessNames?.length?`\n\n🏷️ **Trading as:** ${d.businessNames.join(", ")}`:"";
      const acnLine=d.acn?`\n🆔 **ACN:** ${d.acn}`:"";
      const addressLine=d.address?`\n📍 **Address:** ${d.address}`:"";
      addMessage("bot",`Found it! Here's what the ABR has on file:\n\n**${d.entityName}**${tradingLine}\n\n📋 **ABN:** ${d.abn}${acnLine}\n🏢 **Type:** ${d.entityType}\n✅ **Status:** ${d.status}${addressLine}\n💰 **GST:** ${d.gst}\n\nDoes that all look right to you?`);
      setStep(STEPS.CONFIRM);
    } catch(err){removeTyping();addMessage("bot",`Something went wrong reaching the ABR — ${err.message}. Give it another go in a moment?`);}
    setLoading(false);
  };

  // ─── Confirm ─────────────────────────────────────────────────────────────
  const handleConfirm = (yes) => {
    if(yes){
      addMessage("user","Yes, that's us!");
      setTimeout(()=>{addMessage("bot",`Great, welcome! 👋\n\nNow — **what are you looking to finance?** Describe it as best you can (make, model, year, new or used — whatever you know), and tell me **how much you're after**.`);setStep(STEPS.ASSET);},350);
    } else {
      addMessage("user","No, that's not right.");
      setTimeout(()=>{addMessage("bot","No worries! Pop in the correct ABN and we'll have another look.");setBizData(null);setAbn("");setStep(STEPS.ABN);},350);
    }
  };

  // ─── Asset ───────────────────────────────────────────────────────────────
  const handleAssetSubmit = async () => {
    if(!assetDesc.trim()||!amount.trim())return;
    const rawAmount=amount.replace(/,/g,"");
    addMessage("user",`Looking to finance: ${assetDesc} — seeking $${parseInt(rawAmount).toLocaleString("en-AU")}`);
    setAssetDesc("");setAmount("");setStep(STEPS.ENRICHING);
    await new Promise(r=>setTimeout(r,300));
    addMessage("bot","Nice one! Let me dig into the details on that asset…",{typing:true});
    const result=await enrichAsset(assetDesc,rawAmount).catch(()=>null);
    removeTyping();
    if(!result){addMessage("bot","Hmm, I had trouble analysing that. Could you describe it again with a bit more detail?");setStep(STEPS.ASSET);return;}
    setEnriched({...result,amount:rawAmount});
    setStep(STEPS.ENRICHED);
    addMessage("bot","Here's what I've put together on the asset:",{assetCard:true,data:{...result,amount:rawAmount}});
    if(result.clarifyingQuestions?.length&&result.confidence!=="High"){
      setTimeout(()=>addMessage("bot",`To sharpen this up:\n\n${result.clarifyingQuestions.map((q,i)=>`${i+1}. ${q}`).join("\n")}`),700);
    } else if(isReturnAsset) {
      setTimeout(()=>{
        addMessage("bot","Asset confirmed ✅\n\nI already have all your business and director details. Ready to run the lender match?");
        setStep(STEPS.SUMMARY);
      },700);
    } else {
      setTimeout(()=>addMessage("bot",`Solid! Clear picture of the asset. Next I need a few details about the people behind the business — ready to keep going?`),700);
    }
  };

  // ─── Privacy ─────────────────────────────────────────────────────────────
  const handlePrivacyAccept = () => {
    addMessage("user","I agree to the privacy terms.");
    setTimeout(()=>{addMessage("bot",`Thanks for that. Now — **how many directors or guarantors** will be part of this application?`);setStep(STEPS.DIRECTORS);},350);
  };

  // ─── Director count ───────────────────────────────────────────────────────
  const handleDirectorCount = (count) => {
    setDirectorCount(count);
    setDirectors(Array.from({length:count},()=>emptyDirector()));
    setCurrentDirectorIdx(0);setDirectorDraft(emptyDirector());
    addMessage("user",`${count} director${count>1?"s":""}`);
    setTimeout(()=>addMessage("bot",`Perfect. Let's start with **Director 1**.\n\nI'll need their full legal name, date of birth, residential address, and ownership percentage.`),350);
  };

  // ─── Save director ────────────────────────────────────────────────────────
  const handleDirectorSave = () => {
    if(!directorDraft.name.trim()||!directorDraft.dob.trim()||!directorDraft.address.trim())return;
    const updated=[...directors];updated[currentDirectorIdx]={...directorDraft};setDirectors(updated);
    addMessage("user",`Director ${currentDirectorIdx+1}: ${directorDraft.name}, DOB ${directorDraft.dob}, ${directorDraft.address}${directorDraft.ownership?`, ${directorDraft.ownership}% ownership`:""}${directorDraft.email?`, ${directorDraft.email}`:""}${directorDraft.mobile?`, ${directorDraft.mobile}`:""}`);
    if(currentDirectorIdx+1<directorCount){
      const next=currentDirectorIdx+1;setCurrentDirectorIdx(next);setDirectorDraft(emptyDirector());
      setTimeout(()=>addMessage("bot",`Got it! Now **Director ${next+1}** — full name, DOB, residential address and ownership %.`),350);
    } else {
      setTimeout(()=>{addMessage("bot",`All directors captured ✅\n\nA few quick questions about the business itself — **how long has it been trading?**`);setStep(STEPS.BUSINESS);},350);
    }
  };

  // ─── Business ────────────────────────────────────────────────────────────
  const handleBusinessSubmit = () => {
    if(!business.yearsTrading||!business.turnover||business.atoDebt===null)return;
    addMessage("user",`Trading ${business.yearsTrading} years, turnover ${business.turnover}, ATO debt: ${business.atoDebt?"Yes":"No"}`);
    setTimeout(()=>{addMessage("bot",`Great. Last question — does the business have **any existing finance or lease commitments?**`);setStep(STEPS.LIABILITIES);},350);
  };

  // ─── Liabilities ─────────────────────────────────────────────────────────
const handleLiabilitiesSubmit = () => {
    if(liabilities.hasOther===null)return;
    addMessage("user",liabilities.hasOther?`Yes — ${liabilities.details||"existing commitments"}`:"No existing finance commitments");
    setTimeout(()=>{
      addMessage("bot","Almost done! Do any directors or guarantors own residential property?");
      setStep(STEPS.PROPERTY);
    },350);
  };

  const handlePropertySubmit = () => {
  const msg = propertyOwned==="yes" ? "Yes — directors own residential property."
    : propertyOwned==="spouse" ? "Yes — property is in a spouse's name."
    : "No residential property.";
  addMessage("user", msg);
  setTimeout(()=>{
    addMessage("bot","That's everything. Let me pull together the full application summary…");
    setTimeout(()=>{
      const summary={business:bizData,asset:enriched,directors,businessProfile:{...business},liabilities:{...liabilities},propertyOwned};
      addMessage("bot","Here's the complete profile:",{summaryCard:true,data:summary});
      setTimeout(()=>addMessage("bot","Everything look good? Once you confirm I'll run this against our lender panel. 🎯"),800);
    },600);
    setStep(STEPS.SUMMARY);
  },350);
};

// ─── Download PDF Summary ─────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    const fmt = (n) => `$${parseInt(n).toLocaleString('en-AU')}`;
    const strong = matchResults?.filter(r => r.tier === 'strong') || [];
    const possible = matchResults?.filter(r => r.tier === 'possible') || [];
    const declined = matchResults?.filter(r => r.tier === 'declined') || [];

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>CapitalIQ Application Summary</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a2840; background: #fff; padding: 40px; }
    .header { display: flex; align-items: center; gap: 16px; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; }
    .logo { width: 44px; height: 44px; border-radius: 10px; background: linear-gradient(135deg,#3b7df7,#6d48e5); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; }
    .brand { }
    .brand-name { font-size: 22px; font-weight: 700; color: #1a2840; letter-spacing: -0.03em; }
    .brand-sub { font-size: 12px; color: #64748b; margin-top: 2px; }
    .date { margin-left: auto; font-size: 12px; color: #94a3b8; }
    .section { margin-bottom: 24px; }
    .section-title { font-size: 11px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #64748b; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #f1f5f9; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
    .field { padding: 6px 0; }
    .field-label { font-size: 11px; color: #94a3b8; margin-bottom: 2px; }
    .field-value { font-size: 13px; color: #1a2840; font-weight: 500; }
    .match-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f1f5f9; }
    .match-badge { padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
    .strong { background: #f0fdf4; color: #16a34a; border: 1px solid #86efac; }
    .possible { background: #fffbeb; color: #d97706; border: 1px solid #fcd34d; }
    .declined { background: #fef2f2; color: #dc2626; border: 1px solid #fca5a5; }
    .match-name { font-size: 13px; font-weight: 600; color: #1a2840; }
    .match-gaps { font-size: 12px; color: #64748b; margin-top: 4px; }
    .summary-boxes { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 24px; }
    .summary-box { text-align: center; padding: 16px; border-radius: 8px; }
    .summary-box.s { background: #f0fdf4; border: 1px solid #86efac; }
    .summary-box.p { background: #fffbeb; border: 1px solid #fcd34d; }
    .summary-box.d { background: #fef2f2; border: 1px solid #fca5a5; }
    .summary-num { font-size: 28px; font-weight: 700; }
    .summary-num.s { color: #16a34a; }
    .summary-num.p { color: #d97706; }
    .summary-num.d { color: #dc2626; }
    .summary-label { font-size: 11px; font-weight: 600; margin-top: 4px; }
    .disclaimer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; line-height: 1.6; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">⚡</div>
    <div class="brand">
      <div class="brand-name">CapitalIQ</div>
      <div class="brand-sub">Business Finance Australia</div>
    </div>
    <div class="date">Generated: ${new Date().toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'long', year: 'numeric' })}</div>
  </div>

  <div class="section">
    <div class="section-title">Business Details</div>
    <div class="grid">
      <div class="field"><div class="field-label">Entity Name</div><div class="field-value">${bizData?.entityName || 'N/A'}</div></div>
      <div class="field"><div class="field-label">ABN</div><div class="field-value">${bizData?.abn || 'N/A'}</div></div>
      <div class="field"><div class="field-label">Entity Type</div><div class="field-value">${bizData?.entityType || 'N/A'}</div></div>
      <div class="field"><div class="field-label">GST Status</div><div class="field-value">${bizData?.gst || 'N/A'}</div></div>
      <div class="field"><div class="field-label">Years Trading</div><div class="field-value">${business?.yearsTrading || 'N/A'}</div></div>
      <div class="field"><div class="field-label">Annual Turnover</div><div class="field-value">${business?.turnover || 'N/A'}</div></div>
      <div class="field"><div class="field-label">ATO Debt</div><div class="field-value">${business?.atoDebt ? 'Yes ⚠️' : 'No'}</div></div>
      <div class="field"><div class="field-label">Property Owned</div><div class="field-value">${propertyOwned === 'yes' ? 'Yes' : propertyOwned === 'spouse' ? "Spouse's name" : 'No'}</div></div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Asset Details</div>
    <div class="grid">
      <div class="field"><div class="field-label">Asset Type</div><div class="field-value">${enriched?.assetType || 'N/A'}</div></div>
      <div class="field"><div class="field-label">Category</div><div class="field-value">${enriched?.category || 'N/A'}</div></div>
      <div class="field"><div class="field-label">Description</div><div class="field-value">${[enriched?.make, enriched?.model, enriched?.year].filter(Boolean).join(' ') || 'N/A'}</div></div>
      <div class="field"><div class="field-label">Condition</div><div class="field-value">${enriched?.condition || 'N/A'}</div></div>
      <div class="field"><div class="field-label">Finance Amount</div><div class="field-value">${enriched?.amount ? fmt(enriched.amount) : 'N/A'}</div></div>
      <div class="field"><div class="field-label">Asset Age</div><div class="field-value">${enriched?.age ? `~${enriched.age} years` : 'N/A'}</div></div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Directors / Guarantors</div>
    ${directors?.map((d, i) => `
      <div class="grid" style="margin-bottom:12px;padding-bottom:12px;border-bottom:1px solid #f1f5f9;">
        <div class="field"><div class="field-label">Director ${i+1}</div><div class="field-value">${d.name || 'N/A'}</div></div>
        <div class="field"><div class="field-label">Date of Birth</div><div class="field-value">${d.dob || 'N/A'}</div></div>
        <div class="field"><div class="field-label">Address</div><div class="field-value">${d.address || 'N/A'}</div></div>
        <div class="field"><div class="field-label">Ownership</div><div class="field-value">${d.ownership ? d.ownership + '%' : 'N/A'}</div></div>
      </div>
    `).join('')}
  </div>

  <div class="section">
    <div class="section-title">Lender Match Results</div>
    <div class="summary-boxes">
      <div class="summary-box s"><div class="summary-num s">${strong.length}</div><div class="summary-label" style="color:#16a34a;">Strong Matches</div></div>
      <div class="summary-box p"><div class="summary-num p">${possible.length}</div><div class="summary-label" style="color:#d97706;">Possible Matches</div></div>
      <div class="summary-box d"><div class="summary-num d">${declined.length}</div><div class="summary-label" style="color:#dc2626;">Not Suitable</div></div>
    </div>
    ${matchResults?.map(r => `
      <div class="match-row">
        <div>
          <div style="display:flex;align-items:center;gap:8px;">
            <div class="match-name">${r.lender}</div>
            <span class="match-badge ${r.tier}">${r.tier.charAt(0).toUpperCase() + r.tier.slice(1)}</span>
          </div>
          ${r.gaps?.length ? `<div class="match-gaps">⚠️ ${r.gaps.join(' • ')}</div>` : ''}
          ${r.strengths?.length ? `<div class="match-gaps" style="color:#16a34a;">✓ ${r.strengths.join(' • ')}</div>` : ''}
        </div>
      </div>
    `).join('')}
  </div>

  <div class="disclaimer">
    * This summary was generated by CapitalIQ (iqfins.co) on ${new Date().toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}. 
    Lender match results are indicative only and subject to full credit assessment. 
    This document is confidential and intended solely for the named applicant.
  </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };
  // ─── Start new application ────────────────────────────────────────────────
  const handleResetApplication = () => {
    setStep(STEPS.ABN);
    setAbn("");
    setAbnError("");
    setBizData(null);
    setAssetDesc("");
    setAmount("");
    setEnriched(null);
    setChatLog([]);
    setDirectorCount(null);
    setDirectors([emptyDirector()]);
    setCurrentDirectorIdx(0);
    setDirectorDraft(emptyDirector());
    setBusiness({yearsTrading:"",turnover:"",atoDebt:null});
    setLiabilities({hasOther:null,details:""});
    setMatchResults(null);
    setPropertyOwned(null);
    setSubmitted(false);
    setIsReturnAsset(false);
    setShowResetConfirm(false);
  };
  
  // ─── Finance another asset ────────────────────────────────────────────────
  const handleFinanceAnother = () => {
    setEnriched(null);
    setAssetDesc("");
    setAmount("");
    setMatchResults(null);
    setSubmitted(false);
    setIsReturnAsset(true);
    setChatLog([]);
    addMessage("bot", `Great! Let's find financing for another asset for **${bizData?.entityName}**.\n\nWhat are you looking to finance this time? Describe it as best you can, and tell me how much you're after.`);
    setStep(STEPS.ASSET);
  };
  // ─── Run matching ─────────────────────────────────────────────────────────
  const handleRunMatching = async () => {
    addMessage("user","Run the lender match!");
    setStep(STEPS.MATCHING);
    addMessage("bot","Checking this application against our full lender panel now…",{typing:true});
    await new Promise(r=>setTimeout(r,2200));
    removeTyping();
    const response = await fetch(`${API_BASE}/api/match`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({bizData, enriched, businessProfile: business, directors, liabilities, propertyOwned})
    });
    const results = await response.json();
    const summary = buildMatchSummary(results);
    setMatchResults(results);
    setStep(STEPS.RESULTS);

    // Build the reveal message — no lender names, smart guidance only
    let msg = "";
    if(summary.strong > 0) {
      msg += `✅ **${summary.strong} Strong Match${summary.strong>1?"es":""}** — we have lenders on our panel this deal fits well.\n\n`;
    }
    if(summary.possible > 0) {
      msg += `🔶 **${summary.possible} Possible Match${summary.possible>1?"es":""}** — these could work with a little more information or structuring.\n\n`;
    }
    if(summary.declined > 0 && summary.strong === 0 && summary.possible === 0) {
      msg += `⚠️ This deal needs some work before we can find a home for it.\n\n`;
    }

    if(summary.possibleGaps.length > 0) {
      msg += `**To unlock more options:**\n${summary.possibleGaps.map(g=>`• ${g}`).join("\n")}\n\n`;
    }

    if(summary.strong > 0 || summary.possible > 0) {
      msg += `Ready to submit? Hit the button below and we'll get to work. 🚀`;
    } else {
      msg += `**What's holding this back:**\n${summary.declinedGaps.slice(0,3).map(g=>`• ${g}`).join("\n")}\n\nGive us a call and we'll work through the options with you.`;
    }

    setTimeout(()=>addMessage("bot",msg,{matchCard:true,data:results}),400);
  };

  if (window.location.pathname === '/admin') return <AdminPage />;
  if (mode === 'consumer') return <ConsumerFlow onBack={() => setMode(null)} />;
  
  return (
    <div style={{height:"100vh",background:"#080d18",fontFamily:"'DM Sans',sans-serif",display:"flex",flexDirection:"column",alignItems:"center",overflow:"hidden"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Playfair+Display:wght@600;700&display=swap" rel="stylesheet"/>
      <Styles/>
      <header style={{width:"100%",background:"rgba(8,13,24,0.9)",borderBottom:"1px solid #151f35",padding:"16px 28px",display:"flex",alignItems:"center",gap:12,position:"sticky",top:0,zIndex:10,backdropFilter:"blur(12px)"}}>
        <Logo/>
        <div>
          <div style={{color:"#fff",fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,letterSpacing:"-0.02em"}}>CapitalIQ</div>
          <div style={{color:"#3a5070",fontSize:10,fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase"}}>Business Finance Australia</div>
        </div>
        <StepIndicator step={step}/>
      </header>
      {step===STEPS.RESULTS&&(
        <div style={{position:'fixed',top:80,right:16,width:320,zIndex:100,filter:'drop-shadow(0 4px 24px rgba(0,0,0,0.5))'}}>
          <RepaymentCalculator amount={enriched?.amount} bizData={bizData} enriched={enriched} directors={directors} />
        </div>
      )}
      <div ref={chatRef} style={{width:"100%",maxWidth:740,flex:1,padding:"28px 20px 16px",display:"flex",flexDirection:"column",gap:14,overflowY:"auto"}}>
        {chatLog.length===0&&(mode === 'business' ? <Intro/> : <ModeSelector onSelect={setMode}/>)}
        {chatLog.map(msg=><Message key={msg.id} msg={msg}/>)}
      </div>

      <div style={{width:"100%",maxWidth:740,padding:"0 20px 32px"}}>

        {step===STEPS.ABN&&(
          <Panel>
            <div style={{flex:1}}>
              <input value={abn} onChange={e=>{setAbn(formatABN(e.target.value));setAbnError("");}} onKeyDown={e=>e.key==="Enter"&&!loading&&handleABNLookup()} placeholder="Enter ABN  e.g. 51 824 753 556" style={{...iStyle(abnError),width:"100%"}}/>
              {abnError&&<p style={{color:"#ef4444",fontSize:12,marginTop:5}}>{abnError}</p>}
            </div>
            <button className="btn-primary" onClick={handleABNLookup} disabled={loading||cleanABN(abn).length<11}>{loading?"Searching…":"Look up →"}</button>
          </Panel>
        )}

        {step===STEPS.CONFIRM&&(
          <Panel center>
            <button className="btn-primary" onClick={()=>handleConfirm(true)}>✓ Yes, that's us</button>
            <button className="btn-ghost" onClick={()=>handleConfirm(false)}>✗ Not quite</button>
          </Panel>
        )}

        {step===STEPS.ASSET&&(
          <Panel col>
            <textarea value={assetDesc} onChange={e=>setAssetDesc(e.target.value)} placeholder="e.g. 2022 Kenworth T410 prime mover, ~300,000km, interstate freight…" rows={3} style={{...iStyle(),width:"100%"}}/>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <div style={{position:"relative",flex:1,minWidth:150}}>
                <span style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",color:"#4a6080",fontWeight:600}}>$</span>
                <input value={amount} onChange={e=>setAmount(formatCurrency(e.target.value))} placeholder="Amount sought" style={{...iStyle(),paddingLeft:26,width:"100%"}}/>
              </div>
              <button className="btn-primary" onClick={handleAssetSubmit} disabled={!assetDesc.trim()||!amount.trim()}>Analyse asset →</button>
            </div>
          </Panel>
        )}

       {step===STEPS.ENRICHED&&(
          <Panel center>
            {isReturnAsset ? (
              <button className="btn-primary" onClick={()=>{addMessage("user","Match to lenders!");setTimeout(()=>{setStep(STEPS.SUMMARY);},350);}}>Match to lenders →</button>
            ) : (
              <button className="btn-primary" onClick={()=>{addMessage("user","Let's keep going!");setTimeout(()=>{addMessage("bot",`Before we continue — a quick privacy notice.\n\n**Privacy Notice:** The personal information collected in this application (including director details) is used to assess your finance application. It will be shared with our lending panel and credit reporting bodies as required. You have the right to access and correct this information at any time.\n\nBy proceeding, you confirm all directors have consented to their information being used for this purpose.`);setStep(STEPS.PRIVACY);},350);}}>Continue to credit profile →</button>
            )}
            <button className="btn-ghost" onClick={()=>{addMessage("user","Update asset details.");setTimeout(()=>{addMessage("bot","No worries — describe it again and I'll re-analyse.");setStep(STEPS.ASSET);},350);}}>Update asset</button>
          </Panel>
        )}

        {step===STEPS.PRIVACY&&(
          <Panel center>
            <button className="btn-primary" onClick={handlePrivacyAccept}>✓ I agree — let's proceed</button>
            <button className="btn-ghost" onClick={()=>addMessage("bot","No problem — feel free to call us directly to complete your application.")}>I need more info first</button>
          </Panel>
        )}

        {step===STEPS.DIRECTORS&&directorCount===null&&(
  <Panel col>
    <Lbl>How many directors or guarantors?</Lbl>
    <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
      {[1,2,3,4,5].map(n=>(
        <button key={n} className={n===2?"btn-primary":"btn-ghost"} onClick={()=>handleDirectorCount(n)}>{n}{n===5?"+":""}</button>
      ))}
    </div>
  </Panel>
)}

{step===STEPS.DIRECTORS&&directorCount!==null&&currentDirectorIdx<directorCount&&(
  <Panel col>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div style={{color:"#4a8ef5",fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>Director {currentDirectorIdx+1} of {directorCount}</div>
      <button className="btn-ghost" style={{fontSize:12,padding:"6px 12px"}} onClick={()=>{
        setDirectorCount(null);
        setDirectors([emptyDirector()]);
        setCurrentDirectorIdx(0);
        setDirectorDraft(emptyDirector());
        addMessage("user","Let me change the director count.");
        setTimeout(()=>addMessage("bot","No problem — how many directors or guarantors will be on this application?"),350);
      }}>← Change count</button>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
      <div style={{gridColumn:"1/-1"}}><Lbl>Full Legal Name</Lbl><input value={directorDraft.name} onChange={e=>setDirectorDraft(d=>({...d,name:e.target.value}))} placeholder="e.g. John William Smith" style={{...iStyle(),width:"100%"}}/></div>
      <div><Lbl>Date of Birth</Lbl><input value={directorDraft.dob} onChange={e=>setDirectorDraft(d=>({...d,dob:formatDOB(e.target.value)}))} placeholder="DD/MM/YYYY" style={{...iStyle(),width:"100%"}}/></div>
      <div><Lbl>Ownership %</Lbl><input value={directorDraft.ownership} onChange={e=>setDirectorDraft(d=>({...d,ownership:e.target.value.replace(/\D/g,"").slice(0,3)}))} placeholder="e.g. 50" style={{...iStyle(),width:"100%"}}/></div>
      <div><Lbl>Email Address</Lbl><input value={directorDraft.email||""} onChange={e=>setDirectorDraft(d=>({...d,email:e.target.value}))} placeholder="e.g. john@example.com" style={{...iStyle(),width:"100%"}}/></div>
      <div><Lbl>Mobile Number</Lbl><input value={directorDraft.mobile||""} onChange={e=>setDirectorDraft(d=>({...d,mobile:e.target.value.replace(/\D/g,"").slice(0,10)}))} placeholder="e.g. 0412345678" style={{...iStyle(),width:"100%"}}/></div>
    
      <div style={{gridColumn:"1/-1",position:"relative"}}>
  <Lbl>Residential Address</Lbl>
  <input value={directorDraft.address} onChange={async e=>{
    const val=e.target.value;
    setDirectorDraft(d=>({...d,address:val}));
    if(val.length>=3){
      try{
        const r=await fetch(`${API_BASE}/api/places?input=${encodeURIComponent(val)}`);
        const data=await r.json();
        setAddressSuggestions(data.predictions||[]);
      }catch(e){ setAddressSuggestions([]); }
    } else { setAddressSuggestions([]); }
  }} placeholder="Start typing address…" style={{...iStyle(),width:"100%"}} autoComplete="off"/>
  {addressSuggestions.length>0&&(
    <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#111828",border:"1px solid #1c2d45",borderRadius:10,zIndex:100,marginTop:4,overflow:"hidden"}}>
      {addressSuggestions.map((s,i)=>(
        <div key={i} onClick={async()=>{
          try {
            const r=await fetch(`${API_BASE}/api/places?place_id=${encodeURIComponent(s.place_id)}`);
            const data=await r.json();
            setDirectorDraft(d=>({...d,address:data.address||(typeof s==='string'?s:s.description)}));
          } catch(e){ setDirectorDraft(d=>({...d,address:typeof s==='string'?s:s.description})); }
          setAddressSuggestions([]);
        }}
          style={{padding:"10px 14px",color:"#b8cce8",fontSize:13,cursor:"pointer",borderBottom:i<addressSuggestions.length-1?"1px solid #1c2d45":"none"}}
          onMouseEnter={e=>e.target.style.background="#1a2d45"}
          onMouseLeave={e=>e.target.style.background="transparent"}>
          {typeof s === 'string' ? s : s.description}
        </div>
      ))}
    </div>
  )}
</div>
    </div>
    <button className="btn-primary" onClick={handleDirectorSave} disabled={!directorDraft.name.trim()||!directorDraft.dob.trim()||!directorDraft.address.trim()}>
      Save Director {currentDirectorIdx+1} {currentDirectorIdx+1<directorCount?"→ Next Director":"& Continue →"}
    </button>
  </Panel>
)}
      {step===STEPS.BUSINESS&&(
          <Panel col>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><Lbl>Years Trading</Lbl>
                <select value={business.yearsTrading} onChange={e=>setBusiness(b=>({...b,yearsTrading:e.target.value}))} style={{...iStyle(),width:"100%"}}>
                  <option value="">Select…</option>
                  {["Less than 1","1","2","3","4","5","6","7","8","9","10+"].map(v=><option key={v} value={v}>{v==="Less than 1"?"Less than 1 year":`${v} year${parseInt(v)>1||v==="10+"?"s":""}`}</option>)}
                </select>
              </div>
              <div><Lbl>Annual Turnover (approx)</Lbl>
                <select value={business.turnover} onChange={e=>setBusiness(b=>({...b,turnover:e.target.value}))} style={{...iStyle(),width:"100%"}}>
                  <option value="">Select…</option>
                  {[["Under $250k","Under $250,000"],["$250k–$500k","$250k – $500k"],["$500k–$1m","$500k – $1m"],["$1m–$2m","$1m – $2m"],["$2m–$5m","$2m – $5m"],["$5m+","$5m+"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div style={{gridColumn:"1/-1"}}><Lbl>Any outstanding ATO debt?</Lbl>
                <div style={{display:"flex",gap:10,marginTop:6}}>
                  <button onClick={()=>setBusiness(b=>({...b,atoDebt:false}))} className={business.atoDebt===false?"btn-primary":"btn-ghost"} style={{flex:1}}>No</button>
                  <button onClick={()=>setBusiness(b=>({...b,atoDebt:true}))} className={business.atoDebt===true?"btn-primary":"btn-ghost"} style={{flex:1}}>Yes</button>
                </div>
              </div>
            </div>
            <button className="btn-primary" onClick={handleBusinessSubmit} disabled={!business.yearsTrading||!business.turnover||business.atoDebt===null}>Continue →</button>
          </Panel>
        )}

      {step===STEPS.LIABILITIES&&(
          <Panel col>
            <div><Lbl>Existing finance or lease commitments?</Lbl>
              <div style={{display:"flex",gap:10,marginTop:6}}>
                <button onClick={()=>setLiabilities(l=>({...l,hasOther:false,details:""}))} className={liabilities.hasOther===false?"btn-primary":"btn-ghost"} style={{flex:1}}>No</button>
                <button onClick={()=>setLiabilities(l=>({...l,hasOther:true}))} className={liabilities.hasOther===true?"btn-primary":"btn-ghost"} style={{flex:1}}>Yes</button>
              </div>
            </div>
            {liabilities.hasOther===true&&(
              <div><Lbl>Brief details (lender, approx monthly repayment)</Lbl>
                <textarea value={liabilities.details} onChange={e=>setLiabilities(l=>({...l,details:e.target.value}))} placeholder="e.g. Westpac truck loan ~$2,200/month, Toyota Finance lease ~$800/month" rows={2} style={{...iStyle(),width:"100%",marginTop:6}}/>
              </div>
            )}
            {liabilities.hasOther!==null&&(
              <button className="btn-primary" onClick={handleLiabilitiesSubmit} disabled={liabilities.hasOther&&!liabilities.details.trim()}>Continue →</button>
            )}
          </Panel>
        )}

        {step===STEPS.PROPERTY&&(
          <Panel col>
            <div><Lbl>Do any directors or guarantors own residential property?</Lbl>
              <div style={{display:"flex",gap:10,marginTop:6}}>
                <button onClick={()=>setPropertyOwned("yes")} className={propertyOwned==="yes"?"btn-primary":"btn-ghost"} style={{flex:1}}>Yes</button>
                <button onClick={()=>setPropertyOwned("no")} className={propertyOwned==="no"?"btn-primary":"btn-ghost"} style={{flex:1}}>No</button>
                <button onClick={()=>setPropertyOwned("spouse")} className={propertyOwned==="spouse"?"btn-primary":"btn-ghost"} style={{flex:1}}>Spouse only</button>
              </div>
            </div>
            {propertyOwned!==null&&(
              <button className="btn-primary" onClick={handlePropertySubmit}>Generate Application Summary →</button>
            )}
          </Panel>
        )}

       {step===STEPS.SUMMARY&&(
          <Panel center>
            <button className="btn-primary" onClick={handleRunMatching}>🎯 Match to Lenders →</button>
            <button className="btn-ghost" onClick={()=>{addMessage("user","I need to update something.");setTimeout(()=>addMessage("bot","Of course — which section would you like to update?"),350);}}>Edit details</button>
          </Panel>
        )}

        {step===STEPS.RESULTS&&(
          <Panel center>
            
            <button className="btn-primary" disabled={submitted} onClick={async ()=>{
  setSubmitted(true);
  addMessage("user","Application submitted.");
  addMessage("bot","Submitting your application…",{typing:true});
  try {
    await fetch(`${API_BASE}/api/submit`,{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({business:bizData,asset:enriched,directors,businessProfile:business,liabilities,propertyOwned,matchResults})
    });
  } catch(e){ console.error(e); }
  removeTyping();
  addMessage("bot","✅ Application received! Our team will be in touch within 1 business day. If you'd like to speak to someone sooner, call us directly. 📞 1800 00 25 25");
            if (!isReturnAsset) {
      try {
        await fetch(`${API_BASE}/api/docusign/send`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ business: bizData, directors })
        });
        addMessage("bot", `📝 Privacy consent forms have been sent to ${directors.length > 1 ? 'all directors' : 'the director'} for e-signing.`);
      } catch(e) { console.error('DocuSign error:', e); }
    }
}}>✓ Confirm & submit</button>
            
            <button className="btn-ghost" onClick={()=>{
  addMessage("user","I need to update something.");
  setTimeout(()=>addMessage("bot","Of course — which section would you like to update?",{editMenu:true, setStep, addMessage}),350);
}}>Edit application</button>
            {submitted && (
              <button className="btn-primary" onClick={handleFinanceAnother}>
                ⚡ Finance another asset →
              </button>
            )}
            <button className="btn-ghost" onClick={() => setShowResetConfirm(true)}>
              🔄 Start new application
            </button>

            {showResetConfirm && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
              }}>
                <div style={{
                  background: '#0c1428', border: '1px solid #1b3055', borderRadius: 16,
                  padding: 32, maxWidth: 420, width: '90%'
                }}>
                  <div style={{ fontSize: 28, marginBottom: 12, textAlign: 'center' }}>⚠️</div>
                  <div style={{ color: '#fff', fontSize: 17, fontWeight: 700, textAlign: 'center', marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>
                    Start a New Application?
                  </div>
                  <div style={{ color: '#7aabf8', fontSize: 14, lineHeight: 1.65, textAlign: 'center', marginBottom: 24 }}>
                    This will permanently discard all information entered in the current application — including business details, director information, asset data and lender match results. This action cannot be undone.
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => setShowResetConfirm(false)} style={{
                      flex: 1, padding: '11px', borderRadius: 10, border: '1px solid #1c2d45',
                      cursor: 'pointer', background: 'transparent', color: '#4a6888',
                      fontSize: 14, fontWeight: 600
                    }}>
                      Cancel — Keep Working
                    </button>
                    <button onClick={handleResetApplication} style={{
                      flex: 1, padding: '11px', borderRadius: 10, border: 'none',
                      cursor: 'pointer', background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                      color: '#fff', fontSize: 14, fontWeight: 600
                    }}>
                      Yes, Start Fresh
                    </button>
                  </div>
                </div>
              </div>
            )}
            {submitted && (
              <button className="btn-ghost" onClick={handleDownloadPDF}>
                📄 Download PDF Summary
              </button>
            )}
          </Panel>
        )}

      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function Panel({children,center,col}) {
  return <div style={{background:"#0c1428",border:"1px solid #172035",borderRadius:14,padding:18,display:"flex",flexDirection:col?"column":"row",gap:10,alignItems:center?"center":"flex-start",justifyContent:center?"center":"flex-start",flexWrap:"wrap"}}>{children}</div>;
}
function Lbl({children}) {
  return <div style={{color:"#4a6888",fontSize:11,fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:5}}>{children}</div>;
}
const iStyle = (err) => ({background:"#111828",border:`1.5px solid ${err?"#ef4444":"#1c2d45"}`,borderRadius:10,padding:"11px 14px",color:"#fff",fontSize:14,fontFamily:"'DM Sans',sans-serif"});

function Logo() {
  return (
    <div style={{width:36,height:36,borderRadius:9,background:"linear-gradient(135deg,#3b7df7,#6d48e5)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
      </svg>
    </div>
  );
}

function StepIndicator({step}) {
  const steps=["ABN","Asset","Credit Profile","Lender Match"];
  const map={abn:0,confirm:0,asset:1,enriching:1,enriched:1,privacy:2,directors:2,business:2,liabilities:2,summary:2,matching:3,results:3};
  const idx=map[step]??0;
  return (
    <div className="step-indicator" style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:5}}>
      {steps.map((s,i)=>(
        <div key={s} style={{display:"flex",alignItems:"center",gap:4,opacity:i<=idx?1:0.25}}>
          <div style={{width:22,height:22,borderRadius:"50%",background:i<=idx?"linear-gradient(135deg,#3b7df7,#6d48e5)":"#111828",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:700}}>{i<idx?"✓":i+1}</div>
          <span style={{fontSize:11,color:i<=idx?"#7aabf8":"#2a3d5a",fontWeight:500}}>{s}</span>
          {i<3&&<span style={{color:"#1a2840",fontSize:12}}>›</span>}
        </div>
      ))}
    </div>
  );
}
function ModeSelector({ onSelect }) {
  return (
    <div style={{textAlign:"center",padding:"60px 24px 20px",animation:"fadeUp 0.5s ease"}}>
      <div style={{fontSize:50,marginBottom:16,filter:"drop-shadow(0 0 20px rgba(59,125,247,0.4))"}}>⚡</div>
      <h1 style={{fontFamily:"'Playfair Display',serif",color:"#fff",fontSize:28,fontWeight:700,marginBottom:10,letterSpacing:"-0.03em"}}>Let's get your finance sorted.</h1>
      <p style={{color:"#3a5570",fontSize:15,maxWidth:420,margin:"0 auto 32px",lineHeight:1.65}}>What type of finance are you looking for today?</p>
      <div style={{display:"flex",gap:16,justifyContent:"center",flexWrap:"wrap"}}>
        <button onClick={()=>onSelect('business')} style={{padding:"20px 28px",borderRadius:16,border:"1px solid #172035",background:"#0c1428",cursor:"pointer",minWidth:180,textAlign:"left",transition:"all 0.15s"}}>
          <div style={{fontSize:28,marginBottom:8}}>🏢</div>
          <div style={{color:"#fff",fontSize:15,fontWeight:700,marginBottom:4}}>Business Finance</div>
          <div style={{color:"#3a5570",fontSize:12}}>Equipment, vehicles & commercial assets</div>
        </button>
        <button onClick={()=>onSelect('consumer')} style={{padding:"20px 28px",borderRadius:16,border:"1px solid #172035",background:"#0c1428",cursor:"pointer",minWidth:180,textAlign:"left",transition:"all 0.15s"}}>
          <div style={{fontSize:28,marginBottom:8}}>🚗</div>
          <div style={{color:"#fff",fontSize:15,fontWeight:700,marginBottom:4}}>Personal Finance</div>
          <div style={{color:"#3a5570",fontSize:12}}>Vehicles & personal equipment</div>
        </button>
      </div>
    </div>
  );
}

function Intro() {
  return (
    <div style={{textAlign:"center",padding:"60px 24px 20px",animation:"fadeUp 0.5s ease"}}>
      <div style={{fontSize:50,marginBottom:16,filter:"drop-shadow(0 0 20px rgba(59,125,247,0.4))"}}>⚡</div>
      <h1 style={{fontFamily:"'Playfair Display',serif",color:"#fff",fontSize:28,fontWeight:700,marginBottom:10,letterSpacing:"-0.03em"}}>Let's get your finance sorted.</h1>
      <p style={{color:"#3a5570",fontSize:15,maxWidth:420,margin:"0 auto",lineHeight:1.65}}>We'll verify your business, understand what you need, then match you with the right lenders. Start by entering your ABN below.</p>
    </div>
  );
}

function Message({msg}) {
  const isUser=msg.role==="user";
  return (
    <div className="msg-enter" style={{display:"flex",flexDirection:isUser?"row-reverse":"row",gap:9,alignItems:"flex-end"}}>
      {!isUser&&<div style={{width:30,height:30,borderRadius:9,background:"linear-gradient(135deg,#3b7df7,#6d48e5)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:13}}>⚡</div>}
      <div style={{maxWidth:"82%",display:"flex",flexDirection:"column",gap:6,alignItems:isUser?"flex-end":"flex-start"}}>
        {msg.typing?(
          <div style={{background:"#101b2e",border:"1px solid #192840",borderRadius:"14px 14px 14px 4px",padding:"13px 17px"}}>
            <span className="dot"/><span className="dot"/><span className="dot"/>
          </div>
        ):msg.assetCard?<AssetCard data={msg.data}/>
          :msg.summaryCard?<SummaryCard data={msg.data}/>
          :msg.matchCard?<MatchResultCard data={msg.data}/>
          :msg.editMenu?<EditMenu setStep={msg.setStep} addMessage={msg.addMessage}/>
          :(
          <div style={{background:isUser?"linear-gradient(135deg,#1a3a72,#261e52)":"#101b2e",border:`1px solid ${isUser?"#263880":"#192840"}`,borderRadius:isUser?"14px 14px 4px 14px":"14px 14px 14px 4px",padding:"13px 17px",color:"#b8cce8",fontSize:14,lineHeight:1.75}}>
            <FormattedText text={msg.content}/>
          </div>
        )}
      </div>
    </div>
  );
}

function FormattedText({text}) {
  return (
    <span>
      {text.split("\n").map((line,i,arr)=>(
        <span key={i}>
          {line.split(/(\*\*[^*]+\*\*)/g).map((p,j)=>
            p.startsWith("**")&&p.endsWith("**")?<strong key={j} style={{color:"#dceeff",fontWeight:600}}>{p.slice(2,-2)}</strong>:<span key={j}>{p}</span>
          )}
          {i<arr.length-1&&<br/>}
        </span>
      ))}
    </span>
  );
}

function MatchResultCard({data}) {
  if(!data)return null;
  const strong=data.filter(r=>r.tier==="strong");
  const possible=data.filter(r=>r.tier==="possible");
  const declined=data.filter(r=>r.tier==="declined");
  return (
    <div style={{background:"linear-gradient(135deg,#0c1f3c,#0e1930)",border:"1px solid #1b3055",borderRadius:14,padding:20,maxWidth:520}}>
      <div style={{color:"#fff",fontWeight:700,fontSize:15,marginBottom:18,fontFamily:"'Playfair Display',serif"}}>Lender Panel Results</div>
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        {[{label:"Strong Matches",count:strong.length,color:"#22c55e",bg:"#052010"},{label:"Possible Matches",count:possible.length,color:"#f59e0b",bg:"#1a1000"},{label:"Not Suitable",count:declined.length,color:"#ef4444",bg:"#1a0505"}].map(({label,count,color,bg})=>(
          <div key={label} style={{flex:1,minWidth:120,background:bg,border:`1px solid ${color}30`,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
            <div style={{color:color,fontSize:26,fontWeight:700,lineHeight:1}}>{count}</div>
            <div style={{color:color,fontSize:11,fontWeight:600,marginTop:4,opacity:0.8}}>{label}</div>
          </div>
        ))}
      </div>
      {possible.length>0&&(
        <div style={{marginBottom:16}}>
          <div style={{color:"#f59e0b",fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>To unlock possible matches</div>
          {[...new Set(possible.flatMap(r=>r.gaps))].map((g,i)=>(
            <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:8}}>
              <span style={{color:"#f59e0b",marginTop:1,flexShrink:0}}>→</span>
              <span style={{color:"#b8cce8",fontSize:13,lineHeight:1.55}}>{g}</span>
            </div>
          ))}
        </div>
      )}
      {strong.length>0&&(
        <div style={{background:"#052010",border:"1px solid #22c55e30",borderRadius:10,padding:"12px 14px"}}>
          <div style={{color:"#22c55e",fontSize:12,fontWeight:600}}>✅ We have strong matches on our panel for this deal.</div>
          <div style={{color:"#6088a8",fontSize:12,marginTop:4}}>Our team will review and be in touch within 1 business day.</div>
        </div>
      )}
      {strong.length===0&&possible.length>0&&(
        <div style={{background:"#1a1000",border:"1px solid #f59e0b30",borderRadius:10,padding:"12px 14px"}}>
          <div style={{color:"#f59e0b",fontSize:12,fontWeight:600}}>🔶 We can likely find a home for this deal with some structuring.</div>
          <div style={{color:"#6088a8",fontSize:12,marginTop:4}}>Address the points above and we'll have more options available.</div>
        </div>
      )}
      {strong.length===0&&possible.length===0&&(
        <div style={{background:"#1a0505",border:"1px solid #ef444430",borderRadius:10,padding:"12px 14px"}}>
          <div style={{color:"#ef4444",fontSize:12,fontWeight:600}}>⚠️ This deal needs more work before we can place it.</div>
          <div style={{color:"#6088a8",fontSize:12,marginTop:4}}>Call us to discuss options — there may still be a path forward.</div>
        </div>
      )}
    </div>
  );
}

function RepaymentCalculator({ amount, bizData, enriched, directors }) {
  const [show, setShow] = useState(true);
  const principal = parseInt((amount || '0').toString().replace(/\D/g, '')) || 0;
  const [term, setTerm] = useState(5);
  const [rate, setRate] = useState(8.5);
  const [balloonPct, setBalloonPct] = useState(0);
  const [advance, setAdvance] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);

  const balloonAmt = Math.round(principal * balloonPct / 100);

  const calc = (p, r, y, balloon, isAdvance) => {
    if (!p || !r || !y) return { monthly: 0, fortnightly: 0, weekly: 0 };
    const monthlyRate = r / 100 / 12;
    const n = y * 12;
    // PV of balloon discounted back
    const pvBalloon = balloon / Math.pow(1 + monthlyRate, n);
    const loanAmt = p - pvBalloon;
    // Ordinary annuity (arrears)
    let monthly = loanAmt * monthlyRate * Math.pow(1 + monthlyRate, n) / (Math.pow(1 + monthlyRate, n) - 1);
    // Advance: divide by (1 + r) to shift payment forward
    if (isAdvance) monthly = monthly / (1 + monthlyRate);
    return {
      monthly: Math.round(monthly),
      fortnightly: Math.round(monthly * 12 / 26),
      weekly: Math.round(monthly * 12 / 52),
    };
  };

  const { monthly, fortnightly, weekly } = calc(principal, rate, term, balloonAmt, advance);
  const fmt = (n) => `$${n.toLocaleString('en-AU')}`;
  if (!show) return null;
  return (
    <div style={{ background: 'linear-gradient(135deg,#0c1f3c,#0e1930)', border: '1px solid #1b3055', borderRadius: 14, padding: 20, maxWidth: 520, marginBottom: 4 }}>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 4, fontFamily: "'Playfair Display',serif" }}>Repayment Estimate</div>
      <div style={{ color: '#4a6888', fontSize: 12, marginBottom: 18 }}>Based on {fmt(principal)} finance amount</div>

      {/* Term */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: '#4a6888', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>Loan Term</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[1,2,3,4,5,6,7].map(y => (
            <button key={y} onClick={() => setTerm(y)} style={{
              padding: '6px 11px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
              background: term === y ? 'linear-gradient(135deg,#3b7df7,#6d48e5)' : '#111828',
              color: term === y ? '#fff' : '#4a6888', transition: 'all 0.15s'
            }}>{y}yr</button>
          ))}
        </div>
      </div>

      {/* Rate */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: '#4a6888', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
          Interest Rate: <span style={{ color: '#7aabf8' }}>{rate.toFixed(1)}%</span>
        </div>
        <input type="range" min="4" max="20" step="0.5" value={rate}
          onChange={e => setRate(parseFloat(e.target.value))}
          style={{ width: '100%', accentColor: '#3b7df7', cursor: 'pointer' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#2a3d5a', fontSize: 11, marginTop: 3 }}>
          <span>4%</span><span>20%</span>
        </div>
      </div>

      {/* Balloon */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: '#4a6888', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
          Balloon / Residual (FV)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
            {[0,10,20,30,40,50].map(pct => (
              <button key={pct} onClick={() => setBalloonPct(pct)} style={{
                padding: '6px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: balloonPct === pct ? 'linear-gradient(135deg,#3b7df7,#6d48e5)' : '#111828',
                color: balloonPct === pct ? '#fff' : '#4a6888', transition: 'all 0.15s'
              }}>{pct}%</button>
            ))}
          </div>
          <div style={{ background: '#070f1e', border: '1px solid #1b3055', borderRadius: 8, padding: '6px 12px', color: '#7aabf8', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', minWidth: 90, textAlign: 'right' }}>
            {fmt(balloonAmt)}
          </div>
        </div>
      </div>

      {/* Advance / Arrears */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ color: '#4a6888', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>Payment Timing</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ label: '● Advance', val: true }, { label: '● Arrears', val: false }].map(({ label, val }) => (
            <button key={label} onClick={() => setAdvance(val)} style={{
              padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
              background: advance === val ? 'linear-gradient(135deg,#3b7df7,#6d48e5)' : '#111828',
              color: advance === val ? '#fff' : '#4a6888', transition: 'all 0.15s'
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div style={{ display: 'flex', gap: 10 }}>
        {[['Weekly', weekly], ['Fortnightly', fortnightly], ['Monthly', monthly]].map(([label, val]) => (
          <div key={label} style={{ flex: 1, background: '#070f1e', border: '1px solid #1b3055', borderRadius: 10, padding: '14px 10px', textAlign: 'center' }}>
            <div style={{ color: '#3b7df7', fontSize: 20, fontWeight: 700, lineHeight: 1 }}>{fmt(val)}</div>
            <div style={{ color: '#4a6888', fontSize: 11, fontWeight: 600, marginTop: 5 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ color: '#2a3d5a', fontSize: 11, marginTop: 12, lineHeight: 1.5 }}>
      {!saved ? (
  <button onClick={async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timestamp: new Date().toLocaleString('en-AU', { timeZone: 'Australia/Sydney' }),
          type: 'Quote',
          entityName: bizData?.entityName || '',
          abn: bizData?.abn || '',
          assetDescription: [enriched?.make, enriched?.model, enriched?.year].filter(Boolean).join(' ') || enriched?.category || '',
          financeAmount: amount ? `$${parseInt(amount).toLocaleString('en-AU')}` : '',
          term: `${term} years`,
          rate: `${rate.toFixed(1)}%`,
          balloon: balloonPct > 0 ? `${balloonPct}% ($${balloonAmt.toLocaleString('en-AU')})` : 'None',
          paymentTiming: advance ? 'Advance' : 'Arrears',
          monthlyRepayment: `$${monthly.toLocaleString('en-AU')}`,
          fortnightlyRepayment: `$${fortnightly.toLocaleString('en-AU')}`,
          weeklyRepayment: `$${weekly.toLocaleString('en-AU')}`,
        })
      });
      setSaved(true);
      setTimeout(() => setShow(false), 2000);
    } catch(e) { console.error(e); }
    setSaving(false);
  }} style={{
    width: '100%', padding: '10px', borderRadius: 9, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
    background: 'linear-gradient(135deg,#3b7df7,#6d48e5)', color: '#fff',
    fontSize: 14, fontWeight: 600, marginBottom: 10, opacity: saving ? 0.7 : 1
  }}>
    {saving ? 'Saving…' : '💾 Save Quote'}
  </button>
) : (
  <div style={{ background: '#052010', border: '1px solid #22c55e30', borderRadius: 9, padding: '10px 14px', color: '#22c55e', fontSize: 13, fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>
    ✅ Quote saved
  </div>
)}
        {!emailSent ? (
        <div style={{ marginBottom: 10 }}>
          {!showEmailInput ? (
            <button onClick={() => {
              setEmailTo(directors?.[0]?.email || '');
              setShowEmailInput(true);
            }} style={{
              width: '100%', padding: '10px', borderRadius: 9, border: '1px solid #1c2d45',
              cursor: 'pointer', background: 'transparent', color: '#7aabf8',
              fontSize: 14, fontWeight: 600
            }}>
              📧 Email this quote
            </button>
          ) : (
            <div>
              <div style={{ color: '#4a6888', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
                Confirm email address to send quote to:
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                placeholder="Email address"
                style={{ flex: 1, background: '#111828', border: '1.5px solid #1c2d45', borderRadius: 9, padding: '9px 14px', color: '#fff', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}
              />
              <button onClick={async () => {
                if (!emailTo) return;
                setEmailSending(true);
                try {
                  await fetch(`${API_BASE}/api/email-quote`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      to: emailTo,
                      bizData,
                      enriched,
                      term,
                      rate,
                      balloon: balloonPct,
                      balloonAmt,
                      paymentTiming: advance ? 'Advance' : 'Arrears',
                      monthly,
                      fortnightly,
                      weekly
                    })
                  });
                  setEmailSent(true);
                } catch(e) { console.error(e); }
                setEmailSending(false);
              }} style={{
                padding: '9px 16px', borderRadius: 9, border: 'none', cursor: emailSending ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg,#3b7df7,#6d48e5)', color: '#fff', fontSize: 13, fontWeight: 600,
                opacity: emailSending ? 0.7 : 1
              }}>
                {emailSending ? 'Sending…' : 'Send →'}
              </button>
              <button onClick={() => setShowEmailInput(false)} style={{
                padding: '9px 12px', borderRadius: 9, border: '1px solid #1c2d45',
                cursor: 'pointer', background: 'transparent', color: '#4a6888', fontSize: 13
              }}>✕</button>
            </div>
         </div>
          )}
        </div>
      ) : (
        <div style={{ background: '#052010', border: '1px solid #22c55e30', borderRadius: 9, padding: '10px 14px', color: '#22c55e', fontSize: 13, fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>
          ✅ Quote emailed successfully!
        </div>
      )}
        * Estimate only. Actual repayments depend on lender terms, fees, and credit assessment.
        {balloonAmt > 0 && ` Balloon of ${fmt(balloonAmt)} due at end of term.`}
      </div>
    </div>
  );
}

function AssetCard({data}) {
  const fields=[["Asset Type",data.assetType],["Category",data.category],["Make",data.make],["Model",data.model],["Year / Age",data.year?`${data.year}${data.age?` (~${data.age}yr)`:""}`:""],["Condition",data.condition],["Specs",data.engineOrCapacity],["Licence",data.licenceRequired],["Primary Use",data.primaryUse],["Finance Amount",data.amount?`$${parseInt(data.amount).toLocaleString("en-AU")}`:null]].filter(([,v])=>v&&v!=="null"&&v!=="Unknown");
  const cColor={High:"#22c55e",Medium:"#f59e0b",Low:"#ef4444"}[data.confidence]||"#888";
  return (
    <div style={{background:"linear-gradient(135deg,#0c1f3c,#0e1930)",border:"1px solid #1b3055",borderRadius:14,padding:18,maxWidth:500}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{color:"#4a8ef5",fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase"}}>Asset Profile</span>
        <span style={{fontSize:11,fontWeight:600,color:cColor,background:`${cColor}1a`,padding:"3px 10px",borderRadius:20,border:`1px solid ${cColor}40`}}>{data.confidence} confidence</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 18px",marginBottom:14}}>
        {fields.map(([label,val])=>(
          <div key={label}>
            <div style={{color:"#2a4560",fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:2}}>{label}</div>
            <div style={{color:"#b0ccee",fontSize:13,fontWeight:500}}>{val}</div>
          </div>
        ))}
      </div>
      {data.lenderNotes&&<div style={{background:"#090f1e",border:"1px solid #172640",borderRadius:9,padding:"10px 13px"}}><div style={{color:"#2a4560",fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:5}}>Lender Notes</div><p style={{color:"#6088a8",fontSize:12,lineHeight:1.65}}>{data.lenderNotes}</p></div>}
    </div>
  );
}

function SummaryCard({data}) {
  const {business,asset,directors,businessProfile,liabilities}=data;
  const S=({title,children})=>(<div style={{marginBottom:16}}><div style={{color:"#4a8ef5",fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10,paddingBottom:6,borderBottom:"1px solid #1b3055"}}>{title}</div>{children}</div>);
  const F=({label,value})=>value?(<div style={{marginBottom:8}}><div style={{color:"#2a4560",fontSize:10,fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:2}}>{label}</div><div style={{color:"#b0ccee",fontSize:13}}>{value}</div></div>):null;
  return (
    <div style={{background:"linear-gradient(135deg,#0c1f3c,#0e1930)",border:"1px solid #1b3055",borderRadius:14,padding:20,maxWidth:520}}>
      <div style={{color:"#fff",fontWeight:700,fontSize:15,marginBottom:18,fontFamily:"'Playfair Display',serif"}}>Application Summary</div>
      <S title="Business"><F label="Entity" value={business?.entityName}/><F label="ABN" value={business?.abn}/><F label="Structure" value={business?.entityType}/><F label="GST" value={business?.gst}/><F label="Years Trading" value={businessProfile?.yearsTrading?`${businessProfile.yearsTrading} years`:null}/><F label="Annual Turnover" value={businessProfile?.turnover}/><F label="ATO Debt" value={businessProfile?.atoDebt?"Yes — outstanding":"No"}/></S>
      <S title="Asset to Finance"><F label="Type" value={`${asset?.assetType||""} — ${asset?.category||""}`}/><F label="Description" value={[asset?.make,asset?.model,asset?.year].filter(Boolean).join(" ")}/><F label="Condition" value={asset?.condition}/><F label="Primary Use" value={asset?.primaryUse}/><F label="Finance Amount" value={asset?.amount?`$${parseInt(asset.amount).toLocaleString("en-AU")}`:null}/></S>
      <S title={`Director${directors?.length>1?"s":""} / Guarantor${directors?.length>1?"s":""}`}>
        {directors?.map((d,i)=>(<div key={i} style={{marginBottom:10,padding:"10px 12px",background:"#090f1e",borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}><div><div style={{color:"#dceeff",fontWeight:600,fontSize:13,marginBottom:4}}>{d.name}</div><div style={{color:"#6088a8",fontSize:12}}>DOB: {d.dob}</div><div style={{color:"#6088a8",fontSize:12}}>{d.address}</div>{d.ownership&&<div style={{color:"#6088a8",fontSize:12}}>Ownership: {d.ownership}%</div>} {d.email&&<div style={{color:"#6088a8",fontSize:12}}>Email: {d.email}</div>} {d.mobile&&<div style={{color:"#6088a8",fontSize:12}}>Mobile: {d.mobile}</div>}</div></div>))}
      </S>
      <S title="Existing Liabilities"><F label="Other Finance" value={liabilities?.hasOther?liabilities.details||"Yes — details not provided":"None declared"}/></S>
    </div>
  );
}

function EditMenu({setStep, addMessage}) {
  const sections = [
    { label: "Asset details", step: "asset", msg: "I'd like to update the asset details." },
    { label: "Directors", step: "directors", msg: "I'd like to update the director details." },
    { label: "Business profile", step: "business", msg: "I'd like to update the business profile." },
    { label: "Liabilities", step: "liabilities", msg: "I'd like to update the liabilities." },
  ];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8,maxWidth:300}}>
      {sections.map(({label,step,msg})=>(
        <button key={step} className="btn-ghost" style={{textAlign:"left"}}
          onClick={()=>{
            addMessage("user", msg);
            setTimeout(()=>{
              addMessage("bot",`No problem — let's update your ${label.toLowerCase()}.`);
              setTimeout(()=>setStep(step), 400);
            },350);
          }}>
          → {label}
        </button>
      ))}
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      *{box-sizing:border-box;margin:0;padding:0}
      ::-webkit-scrollbar{width:4px}
      ::-webkit-scrollbar-thumb{background:#1a2d45;border-radius:2px}
      @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
      @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
      .msg-enter{animation:fadeUp 0.3s ease forwards}
      .dot{display:inline-block;width:6px;height:6px;border-radius:50%;background:#4a8ef5;margin:0 2px;animation:blink 1.2s ease infinite}
      .dot:nth-child(2){animation-delay:.2s}
      .dot:nth-child(3){animation-delay:.4s}
      .btn-primary{background:linear-gradient(135deg,#3b7df7,#6d48e5);border:none;color:#fff;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;padding:11px 22px;border-radius:10px;cursor:pointer;transition:all .2s;white-space:nowrap}
      .btn-primary:hover{transform:translateY(-1px);box-shadow:0 5px 18px rgba(59,125,247,.35)}
      .btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none}
      .btn-ghost{background:transparent;border:1.5px solid #192840;color:#6a88a8;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:500;padding:10px 20px;border-radius:10px;cursor:pointer;transition:all .2s;white-space:nowrap}
      .btn-ghost:hover{border-color:#3b7df7;color:#5a9ef7}
      input,textarea,select{outline:none}
      textarea{resize:none}
      select{appearance:none;cursor:pointer}

      @media(max-width:600px){
        header{padding:12px 16px !important}
        header > div:first-child + div > div:first-child{font-size:14px !important}
        .step-indicator span{display:none}
        .step-indicator{gap:4px !important}
        .step-indicator > div{gap:2px !important}
        h1{font-size:22px !important}
        p{font-size:14px !important}
        .btn-primary,.btn-ghost{font-size:13px !important;padding:10px 14px !important;white-space:normal !important}
        textarea{font-size:16px !important}
        input,select{font-size:16px !important}
      }
    `}</style>
  );
}
